import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CircleCheck, CircleMinus, LogIn, Download, ClockAlert, MessageSquare } from 'lucide-react';
import {
  ConfirmDialog, Field, OtpInput, PasswordInput, PhoneInput, Stepper,
  useToast,
  type PasswordRule,
} from '@kollez/design-system';
import { useAcceptPartnerInvite, useInspectPartnerInvite } from '@/lib/queries';
import { useAuthStore } from '@/lib/authStore';

/** /accept-invite — supports two shapes:
 *   - Institution invite (legacy 3-step wizard with 2FA + backup codes)
 *   - Partner invite (?token=<jwt> → fetch partner-invite metadata → set password + activate)
 *
 * We decide by presence of `?token`. If token is set, it's a partner invite. Otherwise render
 * the legacy institution wizard.
 */
type Step = 1 | 2 | 3 | 'done' | 'expired';

const RULES: PasswordRule[] = [
  { label: '8 or more characters', test: (p) => p.length >= 8 },
  { label: 'A number', test: (p) => /\d/.test(p) },
  { label: 'A capital letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'Not your name or "password"', test: (p) => !!p && !/harish|password|kollez/i.test(p) },
];

const BACKUP_CODES = ['4827-1930', '7751-0284', '3319-6620', '9082-4417', '1564-8803', '6240-3371'];

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  if (token) return <PartnerInviteFlow token={token} />;
  return <InstitutionInviteFlow />;
}

function PartnerInviteFlow({ token }: { token: string }) {
  const nav = useNavigate();
  const toast = useToast();
  const setSession = useAuthStore((s) => s.setSession);
  const inspect = useInspectPartnerInvite(token);
  const accept = useAcceptPartnerInvite();

  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');

  const passOk = RULES.every((r) => r.test(password));

  if (inspect.isLoading) {
    return (
      <div className="mx-auto" style={{ maxWidth: 560, padding: '48px 20px' }}>
        <p className="helper">Checking your invite…</p>
      </div>
    );
  }
  if (inspect.error) {
    const msg = inspect.error instanceof Error ? inspect.error.message : 'Invite invalid.';
    return (
      <div className="mx-auto" style={{ maxWidth: 560, padding: '48px 20px', display: 'grid', gap: 16 }} data-testid="partner-invite-error">
        <span className="logo-tile" style={{ width: 52, height: 52, color: 'rgb(var(--ink-warning))' }}>
          <ClockAlert size={20} />
        </span>
        <h1 className="h-page">This invitation didn't work</h1>
        <p className="m-0">{msg}</p>
        <Link className="btn btn-primary justify-self-start" to="/login">Go to sign in</Link>
      </div>
    );
  }

  const invite = inspect.data!;

  return (
    <div
      className="mx-auto"
      style={{ maxWidth: 560, padding: '32px 20px 64px', display: 'grid', gap: 20 }}
      data-testid="partner-invite-form"
    >
      <div className="card grid gap-3" style={{ padding: 22, borderTop: '3px solid rgb(var(--margin))' }}>
        <div className="flex items-center gap-3">
          <span className="logo-tile" style={{ width: 48, height: 48 }}>
            {(invite.partner.name ?? 'W').slice(0, 2).toUpperCase()}
          </span>
          <div>
            <p className="eyebrow m-0">Invitation · valid until {new Date(invite.expiresAt).toLocaleDateString('en-IN')}</p>
            <h1 className="h-section" style={{ fontSize: '1.375rem' }}>Join {invite.partner.name} on Kollez</h1>
          </div>
        </div>
        <p className="m-0">
          You've been invited as <b>{invite.role.replace(/_/g, ' ')}</b> at <b>{invite.partner.name}</b>.
          Set a password below to activate your account. The email <b>{invite.email}</b> is already
          verified through this invite.
        </p>
      </div>

      <div className="card p-5 grid gap-4">
        <h2 className="h-section m-0">Set up your account</h2>
        <Field label="Full name">
          <input
            className="input"
            value={fullName || invite.fullName || ''}
            onChange={(e) => setFullName(e.target.value)}
            placeholder={invite.fullName ?? 'Your full name'}
          />
        </Field>
        <Field label="Phone (optional)">
          <PhoneInput value={phone} onChange={setPhone} placeholder="10 digits" />
        </Field>
        <Field label="Password">
          <PasswordInput
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            rules={RULES}
            showStrength
          />
        </Field>
        <button
          className="btn btn-primary btn-lg"
          data-testid="partner-invite-submit"
          disabled={!passOk || accept.isPending}
          onClick={() => {
            accept.mutate({
              token,
              password,
              fullName: (fullName || invite.fullName || undefined),
              phone: phone || undefined,
            }, {
              onSuccess: (res) => {
                // Hydrate session; partner users have no memberships, no activeTenantId.
                setSession(
                  {
                    id: res.user.id,
                    fullName: res.user.fullName,
                    email: res.user.email,
                    role: res.user.role,
                    tenant: res.user.tenant,
                    partner: res.user.partner,
                    onboardingComplete: false,
                  },
                  res.tokens,
                  { activeTenantId: null, memberships: [] },
                );
                toast.show({ message: `Welcome to ${res.partner.name}.` });
                nav('/company/campus-hiring');
              },
              onError: (err) => {
                toast.show({ tone: 'danger', message: err instanceof Error ? err.message : "Couldn't activate." });
              },
            });
          }}
        >{accept.isPending ? 'Activating…' : 'Activate my account'}</button>
      </div>
    </div>
  );
}

/**
 * Legacy institution-invite wizard — kept intact so existing links (with `?step=` or `?state=`)
 * keep working. This part is not being modified beyond wrapping.
 */
function InstitutionInviteFlow() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const toast = useToast();
  const initial = (params.get('state') === 'expired' ? 'expired' : (params.get('step') as Step) || 1) as Step;
  const [step, setStep] = useState<Step>(initial);
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [agree, setAgree] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [backupSaved, setBackupSaved] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  const passOk = RULES.every((r) => r.test(password));
  const mobOk = mobile.length === 10;
  const step2Ready = passOk && mobOk && agree;
  const otpOk = otp.every((d) => d);

  function submitStep2(e: React.FormEvent) {
    e.preventDefault();
    if (!step2Ready) return;
    setStep(3);
    window.scrollTo(0, 0);
  }

  return (
    <div className="mx-auto" style={{ maxWidth: 560, padding: '32px 20px 64px', display: 'grid', gap: 20 }}>
      <style>{`
        .rules-list { display: grid; gap: 4px; font-size: .8125rem; }
        .rules-list span { display: flex; gap: 6px; align-items: center; color: rgb(var(--ink-tertiary)); }
        .rules-list span.ok { color: rgb(var(--ink-success)); }
        .meter-bar { height: 6px; border-radius: 3px; background: rgb(var(--bg-tertiary)); overflow: hidden; }
        .meter-bar i { display: block; height: 100%; transition: width .2s, background .2s; }
        .qr-box { width: 148px; height: 148px; background: #fff; padding: 8px; border-radius: 8px; border: 1px solid rgb(var(--line-secondary)); display: grid; place-items: center; font-family: monospace; }
        .codes-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px; font: 600 14px/1.6 ui-monospace, Menlo, monospace; }
        .codes-grid span { background: rgb(var(--bg-secondary)); border-radius: 6px; padding: 4px 10px; text-align: center; }
      `}</style>

      {typeof step === 'number' && step <= 3 && (
        <Stepper current={step} steps={[{ label: 'Invitation' }, { label: 'Your account' }, { label: 'Two-step' }]} />
      )}

      {step === 1 && (
        <section className="grid gap-5">
          <div className="card grid gap-3" style={{ padding: 22, borderTop: '3px solid rgb(var(--margin))' }}>
            <div className="flex items-center gap-3">
              <span className="logo-tile" style={{ width: 48, height: 48 }}>GIT</span>
              <div>
                <p className="eyebrow m-0">Invitation · valid until 24 Sept</p>
                <h1 className="h-section" style={{ fontSize: '1.375rem' }}>Join Godavari Institute of Technology</h1>
              </div>
            </div>
            <p className="m-0"><b>Prof. Suresh B.</b>, Principal, invited <b>harish.t@git.edu.in</b> to join as <b>Faculty placement coordinator, CSE</b>.</p>
            <div className="well p-4 grid gap-2 text-sm">
              <b>You'll be able to</b>
              <div className="qcheck ok"><CircleCheck size={14} /><span>See CSE students' records and readiness</span></div>
              <div className="qcheck ok"><CircleCheck size={14} /><span>Run mock interviews and approve recommendations</span></div>
              <div className="qcheck ok"><CircleCheck size={14} /><span>Write and review exam questions</span></div>
              <div className="qcheck"><CircleMinus size={14} className="text-tertiary" /><span>Not release offers or export student data</span></div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button className="btn btn-primary" onClick={() => setStep(2)}>Accept and continue</button>
              <button className="btn btn-ghost" onClick={() => setDeclineOpen(true)}>This isn't for me</button>
            </div>
          </div>
          <p className="helper m-0">kollez will never ask you for money, OTPs or passwords over the phone.</p>
        </section>
      )}

      {step === 2 && (
        <section className="grid gap-5">
          <div>
            <h1 className="h-page">Set up your account</h1>
            <p className="subhead mt-1">For harish.t@git.edu.in</p>
          </div>
          <button className="sso" onClick={() => { if (!agree) { toast.show({ tone: 'warning', message: 'Tick the confidentiality statement first' }); return; } setStep(3); }}>
            <LogIn size={14} />Use my git.edu.in Google account
          </button>
          <div className="or">or create a password</div>
          <form className="grid gap-4" onSubmit={submitStep2} noValidate>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Full name">
                <input className="input" defaultValue="Harish T." autoComplete="name" />
              </Field>
              <Field label="Mobile">
                <PhoneInput value={mobile} onChange={setMobile} placeholder="10 digits" />
              </Field>
            </div>
            <Field label="Password">
              <PasswordInput
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                rules={RULES}
                showStrength
              />
            </Field>
            <label className="flex gap-2 items-start text-sm">
              <input type="checkbox" className="check mt-0.5" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
              I'll keep student data confidential and use it only for placements, as my institution's policy requires.
            </label>
            <button className="btn btn-primary btn-lg" type="submit" disabled={!step2Ready}>Continue</button>
          </form>
        </section>
      )}

      {step === 3 && (
        <section className="grid gap-5">
          <div>
            <h1 className="h-page">Turn on two-step sign-in</h1>
            <p className="subhead mt-1">Required for staff. Use Google Authenticator, Microsoft Authenticator or any similar app.</p>
          </div>
          <div className="card card-pad grid gap-4 sm:grid-cols-[auto_1fr] items-center">
            <div className="qr-box">QR</div>
            <div className="grid gap-2 text-sm">
              <b>1. Scan this with your app</b>
              <span className="helper">Can't scan? Enter this key:</span>
              <code style={{ padding: '6px 10px', background: 'rgb(var(--bg-secondary))', borderRadius: 6, wordBreak: 'break-all' }}>KZ7Q P2MX 4LTR 9HVA</code>
              <b className="mt-2">2. Enter the 6-digit code it shows</b>
              <OtpInput value={otp} onChange={setOtp} />
            </div>
          </div>
          <button className="link text-sm justify-self-start" onClick={() => toast.show({ message: "We'll text a code to your mobile each time you sign in on a new device." })}>
            <MessageSquare size={12} />I don't have a smartphone app — use SMS
          </button>
          {otpOk && (
            <div className="card card-pad grid gap-3">
              <b>Save your backup codes</b>
              <p className="helper m-0">If you lose your phone, each code signs you in once. Keep them somewhere safe — not in your email.</p>
              <div className="codes-grid">
                {BACKUP_CODES.map((c) => <span key={c}>{c}</span>)}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => {
                    const body =
                      `Kollez backup codes\n` +
                      `Generated: ${new Date().toISOString()}\n\n` +
                      `Each code signs you in once. Keep them somewhere safe — not in your email.\n\n` +
                      BACKUP_CODES.join('\n') + '\n';
                    const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'kollez-backup-codes.txt';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    setBackupSaved(true);
                    toast.show({ message: 'Saved kollez-backup-codes.txt to your Downloads folder.' });
                  }}
                ><Download size={12} />Download</button>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="check" checked={backupSaved} onChange={(e) => setBackupSaved(e.target.checked)} />
                  I've saved them
                </label>
              </div>
            </div>
          )}
          <button className="btn btn-primary btn-lg" disabled={!(otpOk && backupSaved)} onClick={() => setStep('done')}>Finish</button>
        </section>
      )}

      {step === 'done' && (
        <section className="grid gap-4">
          <span className="logo-tile" style={{ width: 52, height: 52, color: 'rgb(var(--ink-success))', borderColor: 'rgb(var(--ink-success))' }}>
            <CircleCheck size={20} />
          </span>
          <h1 className="h-page">You're in, Harish.</h1>
          <p className="subhead m-0">Your account is ready. A short checklist will help you set up your mentees and first mock interviews.</p>
          <Link className="btn btn-primary justify-self-start" to="/onboarding?as=faculty">Go to my checklist</Link>
        </section>
      )}

      {step === 'expired' && (
        <section className="grid gap-4">
          <span className="logo-tile" style={{ width: 52, height: 52, color: 'rgb(var(--ink-warning))' }}>
            <ClockAlert size={20} />
          </span>
          <h1 className="h-page">This invitation has expired</h1>
          <p className="m-0">Invitations work for 7 days. This one was sent by Prof. Suresh B. on 3 Sept.</p>
          <div className="flex gap-2 flex-wrap">
            <button className="btn btn-primary" onClick={() => toast.show({ message: 'Asked Prof. Suresh B. to send a new invitation' })}>Ask for a new invitation</button>
            <Link className="btn btn-ghost" to="/login">I already have an account</Link>
          </div>
        </section>
      )}

      <ConfirmDialog
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        title="Decline this invitation?"
        body="We'll tell Prof. Suresh B. The invitation stops working."
        cta="Decline"
        tone="danger"
        onConfirm={() => {
          setDeclineOpen(false);
          toast.show({ message: 'Invitation declined' });
          setTimeout(() => nav('/'), 900);
        }}
      />
    </div>
  );
}
